import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditResourceTypeComposer } from "@/forms/rema/editResourceType/composer"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Settings2, Loader2 } from "lucide-react"
import { useToast } from "@/contexts/ToastContext"

function EditResourceTypePage() {
  const { resourceTypeNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Intentionally fetches the full list then finds client-side. reMa has no
  // getResourceTypeByNumber endpoint (only getResourceTypeByName, keyed on name
  // not number), and getResourceTypes returns the entire table (no server-side
  // pagination), so the record is always present client-side and the list is
  // normally already in cache from the list view. This is NOT the pagination bug
  // fixed on the other domains — switch to a single-record fetch only once the
  // backend adds getResourceTypeByNumber.
  const { data, isLoading } = useApiQuery("getResourceTypes", {})
  const rt = ((data as any)?.content?.resourceTypes || (data as any)?.resourceTypes || []).find(
    (r: any) => String(r.resourceTypeNumber) === String(resourceTypeNumber)
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Settings2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Resource Type</CardTitle>
              <CardDescription className="text-base">Update resource type configuration</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <EditResourceTypeComposer
            showHeader={false}
            initialValues={{
              resourceTypeNumber: rt?.resourceTypeNumber,
              namespace: rt?.namespace,
              resourceTypeCategory: rt?.resourceTypeCategory as any,
              resourceTypeName: rt?.resourceTypeName ?? '',
              resourceTypeDescription: rt?.resourceTypeDescription ?? '',
              parentResourceTypes: rt?.parentResourceTypes ?? [],
              permissions: rt?.permissions ?? [],
            }}
            submitLabel="Save Changes"
            onSuccess={() => navigate({ to: '/rema/resource-types' })}
            onError={(error) => toast({ title: 'Error', description: `Failed to update resource type: ${error.message}`, type: 'error' })}
            onCancel={() => navigate({ to: '/rema/resource-types' })}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/rema/resource-types/$resourceTypeNumber_/edit")({
  staticData: {
    title: 'Edit Resource Type',
    isSecured: true,
  },
  component: EditResourceTypePage,
})
